/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { PER_PAGE_OPTIONS } from "constants/common";
import PerPageSelect from "components/common/PerPageSelect";
import { columnGap8Css } from "assets/css/gaps.css";
import { pagesInfoCss } from "./index.css";
import JumpToPage from "./JumpToPage";
import ArrowButton from "./ArrowButton";

export default function Pagination({
  currentPage = 0,
  lastPage = 1,
  perPage = 50,
  perPageOptions = PER_PAGE_OPTIONS,
  label = "Per page",
  isDisabled = false,
  onChangePage,
  onChangePerPage,
  disabledJumpToPage,
  isCountCapped = false,
}) {
  const goFirstPage = () => {
    onChangePage(0);
  };

  const goLastPage = () => {
    onChangePage(lastPage - 1);
  };

  const onNextPage = () => {
    const nextPage = currentPage + 1;
    onChangePage(nextPage);
  };
  const onPrevPage = () => {
    const prevPage = currentPage - 1;
    onChangePage(prevPage);
  };

  const selectedPerPage = useMemo(
    () => perPageOptions.find((option) => option.value === perPage),
    [perPage, perPageOptions],
  );

  return (
    <div css={columnGap8Css} className="main-text flex-a-c-j-c">
      <span>{label}</span>

      <PerPageSelect value={selectedPerPage} options={perPageOptions} onChange={onChangePerPage} />

      <ArrowButton
        disabled={currentPage === 0 || isDisabled}
        arrowType="forward"
        direction="left"
        onClick={goFirstPage}
      />

      <ArrowButton
        disabled={currentPage === 0 || isDisabled}
        arrowType="standard"
        direction="left"
        onClick={onPrevPage}
      />

      <div css={pagesInfoCss}>
        <span>{currentPage + 1}</span>

        <span>of</span>

        <span>
          {lastPage}
          {isCountCapped && lastPage >= 200 && "+"}
        </span>
      </div>

      <ArrowButton
        disabled={currentPage + 1 === lastPage || isDisabled}
        arrowType="standard"
        direction="right"
        onClick={onNextPage}
      />

      <ArrowButton
        disabled={currentPage + 1 === lastPage || isDisabled}
        arrowType="forward"
        direction="right"
        onClick={goLastPage}
      />

      <JumpToPage
        currentPage={currentPage}
        lastPage={lastPage}
        onChangePage={onChangePage}
        disabled={disabledJumpToPage || isDisabled}
      />
    </div>
  );
}

Pagination.propTypes = {
  isDisabled: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  lastPage: PropTypes.number.isRequired,
  perPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  perPageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  label: PropTypes.string,
  onChangePage: PropTypes.func.isRequired,
  onChangePerPage: PropTypes.func.isRequired,
  disabledJumpToPage: PropTypes.bool,
  isCountCapped: PropTypes.bool,
};
